// Libs
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  FirestoreError,
  Timestamp,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getAuth } from "firebase/auth";

// Local
import {
  MarketingAttachment,
  ExistingMarketingAttachment,
  MarketingAttachmentHelpers,
} from "../models/marketing-attachment";
import { logger } from "../logging";

/**
 * Upload an image file to Firebase Storage and create a marketing attachment record
 */
export async function uploadMarketingAttachment(args: {
  siteKey: string;
  file: File;
  alt?: string;
  description?: string;
}): Promise<ExistingMarketingAttachment> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User must be authenticated to upload attachments");
  }

  // Validate file type (images only)
  if (!args.file.type.startsWith("image/")) {
    throw new Error("Only image files are allowed");
  }

  // Validate file size (max 10MB for images)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (args.file.size > maxSize) {
    throw new Error("File size must be less than 10MB");
  }

  try {
    // Generate unique filename
    const timestamp = Date.now();
    const sanitizedName = args.file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
    const fileName = `${timestamp}_${sanitizedName}`;

    // Upload to Firebase Storage
    const storage = getStorage();
    const storageRef = ref(
      storage,
      `marketingAttachments/${args.siteKey}/${fileName}`,
    );

    const uploadResult = await uploadBytes(storageRef, args.file);
    const downloadURL = await getDownloadURL(uploadResult.ref);

    // Create attachment record in Firestore
    const db = getFirestore();
    const attachmentsRef = collection(
      db,
      `siteKeys/${args.siteKey}/marketingAttachments`,
    );

    const attachmentData: MarketingAttachment = {
      url: downloadURL,
      filename: args.file.name,
      contentType: args.file.type,
      size: args.file.size,
      alt: args.alt,
      description: args.description,
      timestampCreated: Timestamp.now(),
      createdBy: user.uid,
      deleted: false,
    };

    const docRef = await addDoc(attachmentsRef, attachmentData);
    const snapshot = await getDoc(docRef);

    return MarketingAttachmentHelpers.createMarketingAttachmentFromSnapshot(
      snapshot,
    );
  } catch (error) {
    logger.error("Error uploading marketing attachment:", error);
    throw new Error("Failed to upload image. Please try again.");
  }
}

/**
 * Get all marketing attachments for a site
 */
export async function getAllMarketingAttachments(
  siteKey: string,
): Promise<ExistingMarketingAttachment[]> {
  try {
    const db = getFirestore();
    const attachmentsRef = collection(
      db,
      `siteKeys/${siteKey}/marketingAttachments`,
    );
    const q = query(
      attachmentsRef,
      where("deleted", "!=", true),
      orderBy("timestampCreated", "desc"),
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      MarketingAttachmentHelpers.createMarketingAttachmentFromSnapshot,
    );
  } catch (error) {
    logger.error("Error getting marketing attachments:", error);
    throw new Error("Failed to load images");
  }
}

/**
 * Get a specific marketing attachment
 */
export async function getMarketingAttachment(
  siteKey: string,
  attachmentId: string,
): Promise<ExistingMarketingAttachment> {
  try {
    const db = getFirestore();
    const attachmentRef = doc(
      db,
      `siteKeys/${siteKey}/marketingAttachments`,
      attachmentId,
    );
    const snapshot = await getDoc(attachmentRef);

    if (!snapshot.exists()) {
      throw new Error("Image not found");
    }

    return MarketingAttachmentHelpers.createMarketingAttachmentFromSnapshot(
      snapshot,
    );
  } catch (error) {
    logger.error("Error getting marketing attachment:", error);
    throw error;
  }
}

/**
 * Update marketing attachment metadata
 */
export async function updateMarketingAttachment(args: {
  siteKey: string;
  attachmentId: string;
  alt?: string;
  description?: string;
}): Promise<void> {
  try {
    const db = getFirestore();
    const attachmentRef = doc(
      db,
      `siteKeys/${args.siteKey}/marketingAttachments`,
      args.attachmentId,
    );

    const updateData: Partial<MarketingAttachment> = {};
    if (args.alt !== undefined) updateData.alt = args.alt;
    if (args.description !== undefined)
      updateData.description = args.description;

    await updateDoc(attachmentRef, updateData);
  } catch (error) {
    logger.error("Error updating marketing attachment:", error);
    throw new Error("Failed to update image details");
  }
}

/**
 * Delete a marketing attachment (soft delete in Firestore, actual file deletion from Storage)
 */
export async function deleteMarketingAttachment(args: {
  siteKey: string;
  attachmentId: string;
}): Promise<void> {
  try {
    // Get attachment details first
    const attachment = await getMarketingAttachment(
      args.siteKey,
      args.attachmentId,
    );

    // Delete from Firebase Storage
    const storage = getStorage();
    const storageRef = ref(storage, attachment.url);

    try {
      await deleteObject(storageRef);
    } catch (storageError) {
      // Log but don't fail - the file might already be deleted
      logger.warn("Failed to delete file from storage:", storageError);
    }

    // Soft delete in Firestore
    const db = getFirestore();
    const attachmentRef = doc(
      db,
      `siteKeys/${args.siteKey}/marketingAttachments`,
      args.attachmentId,
    );
    await updateDoc(attachmentRef, { deleted: true });
  } catch (error) {
    logger.error("Error deleting marketing attachment:", error);
    throw new Error("Failed to delete image");
  }
}

/**
 * Subscribe to marketing attachments changes
 */
export function subscribeMarketingAttachments(args: {
  siteKey: string;
  onChange: (attachments: ExistingMarketingAttachment[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const db = getFirestore();
  const attachmentsRef = collection(
    db,
    `siteKeys/${args.siteKey}/marketingAttachments`,
  );
  const q = query(
    attachmentsRef,
    where("deleted", "!=", true),
    orderBy("timestampCreated", "desc"),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      try {
        const attachments = snapshot.docs.map(
          MarketingAttachmentHelpers.createMarketingAttachmentFromSnapshot,
        );
        args.onChange(attachments);
      } catch (error) {
        logger.error("Error processing marketing attachments snapshot:", error);
        if (args.onError) {
          args.onError(error as FirestoreError);
        }
      }
    },
    (error) => {
      logger.error("Error in marketing attachments subscription:", error);
      if (args.onError) {
        args.onError(error);
      }
    },
  );
}
